import * as React from 'react'
import * as Sentry from '@sentry/nextjs'
import { isLoggedInSelector } from 'auth/selectors'
import { usePaymentsDialog } from 'payments/components/payments-dialog-wrapper-context'
import { useStartFreeTrial } from 'payments/start-free-trial-button/hooks/use-start-free-trial'
import { useFeatureIsOn } from 'growthbook'
import { PaymentStatus } from 'codegen/types'

import {
  useHasAllAccessQuery,
  useUser,
  useUserIsEligibleForAllAccessFreeTrialQuery,
  useUserIsEligibleForAllAccessYearlyUpgradeQuery,
} from '@repo/cf-user/hooks/use-user-data'

function downloadAsset(fileUrl: string) {
  const link = document.createElement('a')
  link.style.display = 'none'
  link.href = fileUrl

  document.body.appendChild(link)
  link.click()

  link.parentNode?.removeChild(link)
}

function mapWpPaymentStatusToPaymentErrorStatus(status: string): PaymentStatus {
  switch (status) {
    case 'PAYMENTSTATUS_SUCCEEDED': {
      return PaymentStatus.Succeeded
    }
    case 'PAYMENTSTATUS_PROCESSING': {
      return PaymentStatus.Processing
    }
    case 'PAYMENTSTATUS_REQUIRES_ACTION': {
      return PaymentStatus.RequiresAction
    }
    default: {
      return PaymentStatus.Failed
    }
  }
}

interface PaymentError {
  success: boolean
  /**
   * Payment URL if the product would fail to download
   */
  url: string
  /**
   * User friendly API messag
   */
  message: string
  status: PaymentStatus
}

interface DownloadLegacyProductResponse {
  url?: string
}

export function useDownloadLegacyProduct({
  type = 'product',
}: { type?: 'product' | 'coins' | 'daily-gift' } = {}) {
  const [isDownloadingAssets, setIsDownloadingAssets] = React.useState(false)
  const [paymentError, setPaymentError] = React.useState<PaymentError | null>(null)
  const { data: userData } = useUser()
  const { data: userHasAllAccess } = useHasAllAccessQuery()
  const { data: isEligibleForAllAccessFreeTrial } = useUserIsEligibleForAllAccessFreeTrialQuery()
  const { data: isEligibleForAllAccessYearlyUpgrade } =
    useUserIsEligibleForAllAccessYearlyUpgradeQuery()
  const isLoggedIn = isLoggedInSelector({ user: userData })
  const { open: openPayments } = usePaymentsDialog()
  const { startFreeTrial, isPending } = useStartFreeTrial()
  const enablePrecheck = useFeatureIsOn('enable-designer-product-download-precheck')

  const handleProductDownload = async ({
    productId,
    isFree = false,
    onSuccess,
  }: {
    productId: string
    isFree?: boolean
    onSuccess?: () => void
  }) => {
    setPaymentError(null)
    if (!isLoggedIn) {
      return
    }

    if (!isFree) {
      //If eligible for free trial, open the payments dialog with free trial option
      if (!userHasAllAccess && isEligibleForAllAccessFreeTrial) {
        await startFreeTrial()
        return
      }

      //If not eligible for free trial but eligible for yearly upgrade, open the payments dialog
      if (!userHasAllAccess && isEligibleForAllAccessYearlyUpgrade) {
        openPayments('yearly')
        return
      }
    }

    //At this point if the product is free or the user has All Access and we can start the download process
    setIsDownloadingAssets(true)
    let response: Response | null = null
    try {
      if (enablePrecheck) {
        response = await fetch(
          `/api/download-legacy-product-with-precheck/?id=${productId}&type=${type}`,
        )
      } else {
        response = await fetch(`/api/download-legacy-product/?id=${productId}&type=${type}`)
      }

      if (!response.ok) {
        // If status is 400, try to parse the error response
        if (response.status === 400) {
          try {
            const error = (await response.json()) as PaymentError
            setPaymentError({
              success: error.success,
              message: error.message,
              status: mapWpPaymentStatusToPaymentErrorStatus(error.status),
              url: error.url,
            })
            return
          } catch {
            setPaymentError({
              success: false,
              message: 'Download URL is not accessible',
              status: PaymentStatus.Failed,
              url: '',
            })
            return
          }
        }
        // For other error statuses
        setPaymentError({
          success: false,
          message: 'Failed to download product',
          status: PaymentStatus.Failed,
          url: '',
        })
        return
      }

      const json = (await response.json()) as DownloadLegacyProductResponse

      if (!json.url) {
        setPaymentError({
          success: false,
          message: 'Download URL is not available',
          status: PaymentStatus.Failed,
          url: '',
        })
        return
      }

      downloadAsset(json.url)

      //if a callback is provided, call it after the download is complete and the download count is increased
      //this can be used to refetch any data that depends on the download count
      if (onSuccess) {
        onSuccess()
      }
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (e) {
      Sentry.captureException(e)
      setPaymentError({
        success: false,
        message: 'Failed to download product',
        status: PaymentStatus.Failed,
        url: '',
      })
    } finally {
      setIsDownloadingAssets(false)
    }
  }

  const resetPaymentError = () => {
    setPaymentError(null)
  }
  return {
    isLoading: isDownloadingAssets || isPending,
    download: handleProductDownload,
    paymentError,
    resetPaymentError,
  }
}
